import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../util/axiosBaseQuery";
import { CreateVenueReservationData, ReedemCouponData, UpdateReservationPayload, VenueReservationResponse } from "../types";
import { VerifyPinResponseData } from "../../types/bookingCreation";
import { apartmentBookingApiSlice } from "../apartment/apartmentApiSlice";

/**
 * Invalidate apartment-list caches in the apartmentBookingApiSlice after a
 * reservation mutation. RTK Query's `invalidatesTags` only operates within
 * the same slice, so cross-slice invalidation must be dispatched explicitly.
 *
 * Without this, a user creating or cancelling a reservation and then
 * navigating back to the apartment list would see stale availability:
 * a reserved apartment still showing as available, or a cancelled one
 * still hidden.
 */
const invalidateApartmentLists = async (
  _arg: unknown,
  { dispatch, queryFulfilled }: { dispatch: any; queryFulfilled: Promise<unknown> }
) => {
  try {
    await queryFulfilled;
    dispatch(
      apartmentBookingApiSlice.util.invalidateTags([
        // "apartmentList" is gone with the endpoint that provided it: the venue
        // selection step no longer fetches its own list, so nothing provides
        // that tag and invalidating it would be a no-op (the inverse of the
        // RTK tag-consistency rule in CLAUDE.md).
        "apartmentByCityList",
        "apartmentByCityAndDistrictList",
        "apartmentDetails",
      ])
    );
  } catch {
    // mutation failed — leave caches untouched
  }
};

export const reservationsApiSlice = createApi({
  reducerPath: "reservationsApiSlice",
  baseQuery: axiosBaseQuery(),
  // tagTypes intentionally minimal — historical entries like "reservationList",
  // "reservation", "userOrders" were declared but never provided by any query
  // in this slice, so the previous `invalidatesTags: ["reservationList"]`
  // declarations were silent no-ops. Cross-slice invalidation is now done via
  // `invalidateApartmentLists` in onQueryStarted.
  tagTypes: [],
  endpoints: (builder) => ({
    createNewReservation: builder.mutation({
      query: (data) => ({
        url: `/reservation`,
        method: "POST",
        data,
      }),
      onQueryStarted: invalidateApartmentLists,
    }),
    venueCreateNewReservation: builder.mutation<VenueReservationResponse, CreateVenueReservationData>({
      query: (data) => {
        return {
          url: `/orders/from-reservation`,
          method: "POST",
          data: data,
        };
      },
    }),
    createParcelCustomerReservation: builder.mutation({
      query: (data) => {
        return {
          url: `/orders/parcel-delivery`,
          method: "POST",
          data: data,
        };
      },
    }),
    removeReservation: builder.mutation({
      query: ({ reservationId }) => ({
        url: `/reservation/${reservationId}`,
        method: "DELETE",
      }),
      onQueryStarted: invalidateApartmentLists,
    }),
    applyCoupon: builder.mutation({
      query: ({ reservationIds, couponCode, email }: ReedemCouponData) => {
        const data: { code: string; email?: string } = {
          code: couponCode,
        };
        
        if (email) {
          data.email = email;
        }
        
        return {
          url: `/reservations/${reservationIds.join(",")}/voucher`,
          method: "POST",
          data,
        };
      },
    }),
    updateReservation: builder.mutation({
      query: ({ reservationId, ...data }: UpdateReservationPayload) => {
        return {
            url: `/reservation/update-and-apply/${reservationId}`,
            method: "PATCH",
            data: data,
        };
      },
    }),

    //specify response type
    verifyPin: builder.query({
      query: ({ verificationCode }: { verificationCode: string } ) => {
        return {
          url: `/groups/order/short-code/${verificationCode}`,
          method: "GET",
        };
      },
      transformResponse: (response: VerifyPinResponseData) => {
        return response;
      },
    }),
    openLocker: builder.mutation({
      query: ({
        assetId,
        orderItemId,
        verificationCode,
      }: {
        assetId: string;
        orderItemId: string;
        verificationCode: string;
      }) => {
        return {
          url: `/open/door/asset/${assetId}`,
          method: "POST",
          data: {
            orderItemId: orderItemId,
            verificationCode: verificationCode,
          },
        };
      },
    }),
  }),
});

export const {
  useCreateNewReservationMutation,
  useRemoveReservationMutation,
  useApplyCouponMutation,
  useUpdateReservationMutation,
  useVenueCreateNewReservationMutation,
  useCreateParcelCustomerReservationMutation,
  useVerifyPinQuery,
  useOpenLockerMutation,
} = reservationsApiSlice;
